/**
 * PCE Template - Shopify Native Client-Side Filtering
 * Provides progressive enhancement for filtering already-rendered products
 * No external API calls - all products rendered server-side by Shopify
 *
 * Uses custom data-pce-filter attributes to avoid conflicts with theme's
 * global data-action handlers which cause URL navigation issues.
 */

class PCETemplate extends HTMLElement {
    constructor() {
        super();

        this.targetTag = this.dataset.targetTag;
        this.activeFilters = new Set([this.targetTag]);

        this.filterList = this.querySelector('.filter-list-wrapper');
        this.productList = this.querySelector('[data-products-container]');
        this.productItems = [];
        this.usePreloader = false;

        // Update product list reference dynamically
        this.updateProductList();

        this.initFilters();
        this.initMobilePanel();

        // Listen for new products loaded via pagination
        this.observeProductChanges();

        // Listen for preloader completion
        this.listenForPreloader();
    }

    /**
     * Update product list (called initially and after pagination loads)
     */
    updateProductList() {
        this.productItems = Array.from(this.productList.querySelectorAll('.product-item'));

        // Store original product data
        this.productData = this.productItems.map(product => ({
            element: product,
            tags: this.getProductTags(product)
        }));
    }

    /**
     * Observe for new products being added (pagination)
     */
    observeProductChanges() {
        const observer = new MutationObserver(() => {
            this.updateProductList();
            this.updateFilterVisibility();
        });

        observer.observe(this.productList, {
            childList: true,
            subtree: false
        });
    }

    /**
     * Extract product tags from the product element
     */
    getProductTags(productElement) {
        const tags = new Set();

        // Get tags from product's data attributes if available
        const productTags = productElement.dataset.tags;
        if (productTags) {
            productTags.split(',').forEach(tag => tags.add(tag.trim()));
        }

        return tags;
    }

    /**
     * Initialize filter controls - PURE PCE, no theme conflicts
     */
    initFilters() {
        // Find all PCE filter elements (data-pce-filter="toggle")
        const filterElements = this.querySelectorAll('[data-pce-filter="toggle"]');

        filterElements.forEach(element => {
            if (element.tagName === 'INPUT') {
                // Checkboxes and radio buttons
                element.addEventListener('change', (e) => {
                    e.stopPropagation(); // Prevent bubbling to theme handlers
                    this.handleFilterChange(e);
                });
            } else {
                // Buttons (color swatches, remove buttons)
                element.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleFilterChange(e);
                });
            }
        });

        // Clear all filters buttons
        const clearButtons = this.querySelectorAll('[data-pce-filter="clear-all"]');
        clearButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.clearFilters();
            });
        });

        // Hide filters that have no matching products
        this.updateFilterVisibility();
    }

    /**
     * Handle filter checkbox/button changes
     */
    handleFilterChange(event) {
        const element = event.target;
        const tag = element.dataset.filterTag || element.dataset.tagUser;

        if (!tag) return;

        console.log('Filter clicked:', tag);

        const filterGroup = element.closest('.collection__filter-checkbox-list, .color-swatch-list__inner');

        // Handle different input types
        if (element.tagName === 'INPUT') {
            if (element.type === 'checkbox') {
                if (element.checked) {
                    // For checkboxes in same group, uncheck siblings (single selection)
                    if (filterGroup) {
                        const siblings = filterGroup.querySelectorAll('input[type="checkbox"]');
                        siblings.forEach(sibling => {
                            if (sibling !== element && sibling.checked) {
                                const siblingTag = sibling.dataset.filterTag || sibling.dataset.tagUser;
                                this.activeFilters.delete(siblingTag);
                                sibling.checked = false;
                            }
                        });
                    }
                    this.activeFilters.add(tag);
                } else {
                    this.activeFilters.delete(tag);
                }
            } else if (element.type === 'radio') {
                // Radio buttons: clear all in group, add this one
                if (filterGroup) {
                    const siblings = filterGroup.querySelectorAll('input[type="radio"]');
                    siblings.forEach(sibling => {
                        const siblingTag = sibling.dataset.filterTag || sibling.dataset.tagUser;
                        this.activeFilters.delete(siblingTag);
                    });
                }
                this.activeFilters.add(tag);
            }
        } else if (element.tagName === 'BUTTON' || element.closest('button')) {
            // Button clicks (color swatches, remove buttons)
            const button = element.tagName === 'BUTTON' ? element : element.closest('button');
            const buttonTag = button.dataset.filterTag || button.dataset.tagUser;

            if (this.activeFilters.has(buttonTag)) {
                // Remove filter
                this.activeFilters.delete(buttonTag);
                button.classList.remove('is-selected');

                // Uncheck corresponding checkbox if exists
                const checkbox = this.querySelector(`input[data-filter-tag="${buttonTag}"]`);
                if (checkbox) checkbox.checked = false;
            } else {
                // Add filter, uncheck siblings
                if (filterGroup) {
                    const siblings = filterGroup.querySelectorAll('[data-pce-filter="toggle"]');
                    siblings.forEach(sibling => {
                        if (sibling !== button) {
                            const siblingTag = sibling.dataset.filterTag || sibling.dataset.tagUser;
                            this.activeFilters.delete(siblingTag);
                            sibling.classList.remove('is-selected');
                        }
                    });
                }
                this.activeFilters.add(buttonTag);
                button.classList.add('is-selected');
            }
        }

        // Ensure target tag is always active
        if (!this.activeFilters.has(this.targetTag)) {
            this.activeFilters.add(this.targetTag);
        }

        console.log('Active filters:', Array.from(this.activeFilters));

        this.updateActiveFilterDisplay();
        this.filterProducts();
    }

    /**
     * Clear all filters except target tag
     */
    clearFilters() {
        console.log('Clearing filters');

        // Clear all filters except the target tag
        this.activeFilters.clear();
        this.activeFilters.add(this.targetTag);

        // Uncheck all inputs
        const allInputs = this.querySelectorAll('[data-pce-filter="toggle"]');
        allInputs.forEach(input => {
            if (input.tagName === 'INPUT') {
                input.checked = false;
            } else {
                input.classList.remove('is-selected');
            }
        });

        this.updateActiveFilterDisplay();
        this.filterProducts();
    }

    /**
     * Update active filter display (chips at top)
     */
    updateActiveFilterDisplay() {
        const activeFilterContainer = this.querySelector('.collection__active-filters');
        if (!activeFilterContainer) return;

        // Clear existing active filter tags
        activeFilterContainer.innerHTML = '';

        // Add active filter tags (excluding target tag)
        const filtersToShow = Array.from(this.activeFilters).filter(f => f !== this.targetTag);

        filtersToShow.forEach(filterTag => {
            const filterElement = document.createElement('div');
            filterElement.className = 'collection__active-filter-item';

            const displayName = filterTag.replace(/-/g, ' ').replace(/_/g, ' ');

            filterElement.innerHTML = `
                <button class="collection__active-filter-cross" data-pce-filter="toggle" data-filter-tag="${filterTag}">
                    <svg focusable="false" class="icon icon--close" viewBox="0 0 19 19" role="presentation">
                        <path d="M9.1923882 8.39339828l7.7781745-7.7781746 1.4142136 1.41421357-7.7781746 7.77817459 7.7781746 7.77817456L16.9705627 19l-7.7781745-7.7781746L1.41421356 19 0 17.5857864l7.7781746-7.77817456L0 2.02943725 1.41421356.61522369 9.1923882 8.39339828z" fill="currentColor" fill-rule="evenodd"></path>
                    </svg>
                </button>
                ${displayName}
            `;

            // Bind event to remove button
            const removeButton = filterElement.querySelector('.collection__active-filter-cross');
            removeButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleFilterChange({ target: removeButton });
            });

            activeFilterContainer.appendChild(filterElement);
        });
    }

    /**
     * Filter products based on active filters
     */
    filterProducts() {
        let visibleCount = 0;

        console.log('Filtering products with:', Array.from(this.activeFilters));

        // If preloader is available and complete, use it for instant filtering
        if (this.usePreloader && window.pcePreloader && window.pcePreloader.isPreloadComplete()) {
            visibleCount = this.filterWithPreloader();
        } else {
            // Fallback to original filtering
            this.productData.forEach(({ element, tags }) => {
                // Check if product matches ALL active filters (AND logic)
                const matchesFilters = Array.from(this.activeFilters).every(filter => {
                    return this.productMatchesFilter(element, filter);
                });

                if (matchesFilters) {
                    element.style.display = '';
                    element.removeAttribute('hidden');
                    visibleCount++;
                } else {
                    element.style.display = 'none';
                    element.setAttribute('hidden', '');
                }
            });
        }

        console.log('Visible products:', visibleCount);

        // Show/hide empty state
        this.updateEmptyState(visibleCount);

        // Re-run out-of-stock sorter if available
        if (window.CollectionPerformance?.OutOfStockSorter) {
            setTimeout(() => {
                new window.CollectionPerformance.OutOfStockSorter();
            }, 50);
        }
    }

    /**
     * Filter products using preloader for instant results
     */
    filterWithPreloader() {
        const preloader = window.pcePreloader;

        // Apply filters to ALL products (including preloaded ones)
        const visibleCount = preloader.applyFiltersToAll((product) => {
            // Check if product matches ALL active filters
            return Array.from(this.activeFilters).every(filter => {
                return this.productMatchesFilter(product, filter);
            });
        });

        // Auto-load more products if filtered results are sparse
        if (visibleCount < 12 && preloader.preloadComplete) {
            this.autoLoadFilteredProducts();
        }

        return visibleCount;
    }

    /**
     * Auto-load more filtered products from cache
     */
    autoLoadFilteredProducts() {
        const preloader = window.pcePreloader;
        const allProducts = preloader.getAllProducts();
        let addedCount = 0;
        const targetCount = 24; // Try to show at least 24 products

        for (const product of allProducts) {
            // Skip if already in DOM
            if (product.parentElement === this.productList) continue;

            // Check if matches filters
            const matchesFilters = Array.from(this.activeFilters).every(filter => {
                return this.productMatchesFilter(product, filter);
            });

            if (matchesFilters) {
                product.style.display = '';
                this.productList.appendChild(product);
                addedCount++;

                if (addedCount >= targetCount) break;
            }
        }

        if (addedCount > 0) {
            console.log(`Auto-loaded ${addedCount} filtered products from cache`);
            this.updateProductList();
        }
    }

    /**
     * Check if a product matches a filter
     */
    productMatchesFilter(productElement, filter) {
        // Check product's data-tags attribute
        const productTags = productElement.dataset.tags;

        if (!productTags) return false;

        // Normalize filter and product tags for comparison
        const normalizedFilter = filter.toLowerCase().replace(/-/g, ' ').replace(/_/g, ' ');
        const tags = productTags.toLowerCase().split(',').map(t => t.trim());

        // Direct match
        if (tags.includes(filter.toLowerCase())) {
            return true;
        }

        // Check each tag for matches
        for (const tag of tags) {
            const normalizedTag = tag.replace(/-/g, ' ').replace(/_/g, ' ');

            // Exact match
            if (normalizedTag === normalizedFilter) {
                return true;
            }

            // For grouped tags (e.g., "brand_barktec" or "dog-size_small")
            // Check if filter matches the value part
            if (normalizedTag.includes(normalizedFilter)) {
                return true;
            }

            // Split by underscore and check parts
            const tagParts = tag.split('_');
            if (tagParts.length > 1) {
                const value = tagParts[tagParts.length - 1];
                if (value.replace(/-/g, ' ') === normalizedFilter) {
                    return true;
                }
            }

            // Check if filter is part of tag (for partial matches)
            if (tag.includes(filter.toLowerCase())) {
                return true;
            }
        }

        return false;
    }

    /**
     * Update empty state visibility
     */
    updateEmptyState(visibleCount) {
        let emptyState = this.productList.querySelector('.empty-state');

        if (visibleCount === 0 && !emptyState) {
            emptyState = document.createElement('div');
            emptyState.className = 'empty-state empty-state--tight';
            emptyState.innerHTML = '<p class="empty-state__description">No products found for the selected filters.</p>';
            this.productList.appendChild(emptyState);
        } else if (visibleCount > 0 && emptyState) {
            emptyState.remove();
        }
    }

    /**
     * Update filter visibility based on available products
     */
    updateFilterVisibility() {
        const filterGroups = this.querySelectorAll('.collection__filter-group');

        filterGroups.forEach(group => {
            const checkboxes = group.querySelectorAll('.collection__filter-checkbox');
            let hasVisibleFilters = false;

            checkboxes.forEach(checkbox => {
                const input = checkbox.querySelector('input');
                const button = checkbox.querySelector('button[data-pce-filter]');
                const tag = input?.dataset.filterTag || button?.dataset.filterTag;

                if (!tag) return;

                // Check if any product has this tag
                const hasProducts = this.productData.some(({ element }) =>
                    this.productMatchesFilter(element, tag)
                );

                if (hasProducts) {
                    checkbox.style.display = '';
                    hasVisibleFilters = true;
                } else {
                    checkbox.style.display = 'none';
                }
            });

            // Hide entire group if no visible filters
            if (!hasVisibleFilters) {
                group.style.display = 'none';
            }
        });
    }

    /**
     * Initialize mobile filter panel
     */
    initMobilePanel() {
        const filterButton = this.querySelector('.collection__toolbar-item--filter');
        const filterClose = this.querySelector('.filter-list__close');

        if (filterButton) {
            filterButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.showFilterPanel();
            });
        }

        if (filterClose) {
            filterClose.addEventListener('click', (e) => {
                e.preventDefault();
                this.hideFilterPanel();
            });
        }

        // Close filter panel when clicking outside
        this.addEventListener('click', (e) => {
            if (!e.target.closest('.filter-list-wrapper .card__section') &&
                !e.target.closest('.collection__toolbar-item--filter')) {
                this.hideFilterPanel();
            }
        });
    }

    /**
     * Show mobile filter panel
     */
    showFilterPanel() {
        if (!this.filterList) return;

        const filterWrapper = this.filterList.querySelector('.collection__filter-group-list');
        if (filterWrapper) {
            filterWrapper.style.height = (window.innerHeight - 50) + 'px';
            filterWrapper.style.overflowY = 'auto';
        }

        this.filterList.classList.add('active');
    }

    /**
     * Hide mobile filter panel
     */
    hideFilterPanel() {
        if (!this.filterList) return;
        this.filterList.classList.remove('active');
    }

    /**
     * Listen for preloader events
     */
    listenForPreloader() {
        // Listen for preload complete event
        this.parentElement?.addEventListener('pce:preloadComplete', (e) => {
            console.log('PCE Template: Preload complete, enabling instant filtering');
            this.usePreloader = true;

            // Update filter visibility based on ALL products
            this.updateFilterVisibilityWithPreloader();
        });

        // Check if preloader is already complete
        if (window.pcePreloader?.isPreloadComplete()) {
            this.usePreloader = true;
        }
    }

    /**
     * Callback when preload is complete
     */
    onPreloadComplete() {
        this.usePreloader = true;
        this.updateFilterVisibilityWithPreloader();
    }

    /**
     * Update filter visibility based on all products (including preloaded)
     */
    updateFilterVisibilityWithPreloader() {
        if (!window.pcePreloader) return;

        const allProducts = window.pcePreloader.getAllProducts();
        const filterGroups = this.querySelectorAll('.collection__filter-group');

        filterGroups.forEach(group => {
            const checkboxes = group.querySelectorAll('.collection__filter-checkbox');
            let hasVisibleFilters = false;

            checkboxes.forEach(checkbox => {
                const input = checkbox.querySelector('input');
                const button = checkbox.querySelector('button[data-pce-filter]');
                const tag = input?.dataset.filterTag || button?.dataset.filterTag;

                if (!tag) return;

                // Check if any product (including preloaded) has this tag
                const hasProducts = allProducts.some(product =>
                    this.productMatchesFilter(product, tag)
                );

                if (hasProducts) {
                    checkbox.style.display = '';
                    hasVisibleFilters = true;
                } else {
                    checkbox.style.display = 'none';
                }
            });

            // Hide entire group if no visible filters
            if (!hasVisibleFilters) {
                group.style.display = 'none';
            }
        });
    }
}

// Register custom element
customElements.define('pce-template', PCETemplate);
